import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { X, User, Mail, Phone, Building, Calendar } from 'lucide-react';

interface GuestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  guestData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    organization?: string;
    customFields?: { [key: string]: string };
  };
  registrationDate: string;
  eventTitle?: string;
  hackathonTitle?: string;
}

const GuestDetailsModal: React.FC<GuestDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  guestData, 
  registrationDate,
  eventTitle,
  hackathonTitle
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Guest Registration Details
          </DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute right-4 top-4"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="h-10 w-10 text-gray-400" />
              </div>
            </div>
            
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {guestData.firstName} {guestData.lastName}
              </h2>
              <p className="text-sm text-gray-500">Guest User</p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Contact Information</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="font-medium">Email:</span>
                <span className="text-gray-600">{guestData.email}</span>
              </div>
              {guestData.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">Phone:</span>
                  <span className="text-gray-600">{guestData.phone}</span>
                </div>
              )}
              {guestData.organization && (
                <div className="flex items-center gap-2 text-sm">
                  <Building className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">Organization:</span>
                  <span className="text-gray-600">{guestData.organization}</span>
                </div>
              )}
            </div>
          </div>

          {/* Registration Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Registration Information</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="font-medium">Registration Date:</span>
                <span className="text-gray-600">{formatDate(registrationDate)}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium">Event:</span>
                <span className="text-gray-600 ml-2">
                  {eventTitle || hackathonTitle || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          {guestData.customFields && Object.keys(guestData.customFields).length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900">Additional Information</h3>
              <div className="space-y-2">
                {Object.entries(guestData.customFields).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium capitalize">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </span>
                    <span className="text-gray-600 ml-2">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              This user registered as a guest and does not have a full Udyogapramoda account.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuestDetailsModal;
